import * as React from 'react';
import {ForwardedRef} from 'react';
import TablePagination, {tablePaginationClasses, TablePaginationProps,} from '@mui/material/TablePagination';
import {styled} from '@mui/material/styles';
import {
    gridPaginationSelector,
    gridVisibleTopLevelRowCountSelector,
    useGridApiContext,
    useGridRootProps,
    useGridSelector
} from '@mui/x-data-grid-pro';
import {IconButton, Tooltip} from '@mui/material';
import {useTheme} from '@mui/styles';
import {TablePaginationActionsProps} from '@mui/material/TablePagination/TablePaginationActions';
import {KeyboardArrowLeft, KeyboardArrowRight} from '@mui/icons-material';


const GridPaginationRoot = styled(TablePagination)(({theme}) => ({
    [`& .${tablePaginationClasses.selectLabel}`]: {
        display: 'none',
        [theme.breakpoints.up('sm')]: {
            display: 'block',
        },
    },
    [`& .${tablePaginationClasses.input}`]: {
        display: 'none',
        [theme.breakpoints.up('sm')]: {
            display: 'inline-flex',
        },
    },
})) as typeof TablePagination;

export const GridPagination = React.forwardRef<HTMLDivElement, Partial<TablePaginationProps>>(
    function GridPagination(props, ref) {
        const apiRef = useGridApiContext();
        const rootProps = useGridRootProps();
        const paginationModel = useGridSelector(apiRef, gridPaginationSelector);
        const visibleTopLevelRowCount = useGridSelector(apiRef, gridVisibleTopLevelRowCountSelector);

        const rowCount = React.useMemo(
            () => rootProps.rowCount ?? visibleTopLevelRowCount ?? 0,
            [rootProps.rowCount, visibleTopLevelRowCount],
        );

        const lastPage = React.useMemo(
            () => Math.floor(rowCount / (paginationModel.pageSize || 1)),
            [rowCount, paginationModel.pageSize],
        );

        const handlePageSizeChange = React.useCallback(
            (event: React.ChangeEvent<HTMLTextAreaElement | HTMLInputElement>) => {
                const pageSize = Number(event.target.value);
                apiRef.current.setPageSize(pageSize);
            },
            [apiRef],
        );

        const handlePageChange = React.useCallback<TablePaginationProps['onPageChange']>(
            (_, page) => {
                apiRef.current.setPage(page);
            },
            [apiRef],
        );


        return (
            <GridPaginationRoot
                ref={ref}
                component="div"
                count={rowCount}
                page={paginationModel.page <= lastPage ? paginationModel.page : lastPage}
                rowsPerPageOptions={rootProps.rowsPerPageOptions}
                rowsPerPage={paginationModel.pageSize}
                onPageChange={handlePageChange}
                onRowsPerPageChange={handlePageSizeChange}
                ActionsComponent={TablePaginationActions}
                {...apiRef.current.getLocaleText('MuiTablePagination')}
                {...props}
            />
        );
    },
);

const TablePaginationActions = React.forwardRef(function TablePaginationActions(props: TablePaginationActionsProps, ref: ForwardedRef<HTMLDivElement>) {
    const {
        backIconButtonProps,
        count,
        getItemAriaLabel,
        nextIconButtonProps,
        onPageChange,
        page,
        rowsPerPage,
        showFirstButton,
        showLastButton,
        ...other
    } = props;

    const theme = useTheme();

    const handleBackButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, page - 1);
    };

    const handleNextButtonClick = (event: React.MouseEvent<HTMLButtonElement>) => {
        onPageChange(event, page + 1);
    };

    return (
        <div ref={ref} {...other}>
            <Tooltip title={getItemAriaLabel('previous')}>
                <IconButton
                    onClick={handleBackButtonClick}
                    disabled={page === 0}
                    color="inherit"
                    component={'span'}
                    {...backIconButtonProps}
                >
                    {theme.direction === 'rtl' ? <KeyboardArrowRight/> : <KeyboardArrowLeft/>}
                </IconButton>
            </Tooltip>
            <Tooltip title={getItemAriaLabel('next')}>
                <IconButton
                    onClick={handleNextButtonClick}
                    disabled={count !== -1 ? page >= Math.ceil(count / rowsPerPage) - 1 : false}
                    color="inherit"
                    component={'span'}
                    {...nextIconButtonProps}
                >
                    {theme.direction === 'rtl' ? <KeyboardArrowLeft/> : <KeyboardArrowRight/>}
                </IconButton>
            </Tooltip>
        </div>
    );
});